/**
 * SidebarNav — Left sidebar navigation for the new AppShell.
 * Desktop: persistent 240px sidebar. Mobile: drawer opened via hamburger.
 */
import React from 'react';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { versionInfo } from '../config/versionInfo';
import { useMsal } from '@azure/msal-react';
import { useUserRole } from '../contexts/UserRoleContext';
import { useVisibilityFilters, isItemVisible } from '../contexts/VisibilityFiltersContext';
import { getSidebarItems } from '../config/navigation';
import packageJson from '../../package.json';

const SIDEBAR_WIDTH = 240;

function SidebarContent({ onClose, onSearchClick }) {
  const theme = useTheme();
  const location = useLocation();
  const { permissions } = useUserRole();
  const { instance } = useMsal();
  const userEmail = instance.getActiveAccount()?.username || "";
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { showBeta, showPlanned } = useVisibilityFilters();

  const rawItems = getSidebarItems(permissions, userEmail);
  // Categorie/hub verbergen op basis van beta/planned-filter (los van rol-toegang)
  const vis = (it) => isItemVisible(it, { showBeta, showPlanned });
  const items = {
    ...rawItems,
    primary: (rawItems.primary || []).filter(vis),
    bottom: (rawItems.bottom || []).filter(vis),
  };

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const handleClick = () => {
    if (isMobile && onClose) onClose();
  };

  const renderItem = (item) => {
    const active = isActive(item.path);
    const IconComponent = item.Icon;
    return (
      <ListItem key={item.path} disablePadding sx={{ px: 1, py: 0.25 }}>
        <ListItemButton
          component={RouterLink}
          to={item.path}
          onClick={handleClick}
          sx={{
            borderRadius: '0.5rem',
            py: 0.75,
            px: 1.5,
            minHeight: 40,
            bgcolor: active ? 'rgba(99,89,131,0.08)' : 'transparent',
            boxShadow: 'none',
            borderLeft: active ? '3px solid #635983' : '3px solid transparent',
            '&:hover': {
              bgcolor: active ? 'rgba(99,89,131,0.12)' : 'rgba(0,0,0,0.03)',
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            <IconComponent
              fontSize="small"
              sx={{
                color: active ? '#635983' : '#556071',
                fontSize: 20,
              }}
            />
          </ListItemIcon>
          <ListItemText
            primary={item.label}
            primaryTypographyProps={{
              variant: 'body2',
              fontFamily: '"Manrope", system-ui, sans-serif',
              fontWeight: active ? 700 : 500,
              fontSize: '0.85rem',
              letterSpacing: '0.01em',
              color: active ? '#635983' : '#556071',
              noWrap: true,
            }}
          />
        </ListItemButton>
      </ListItem>
    );
  };

  return (
    <Box
      sx={{
        width: SIDEBAR_WIDTH,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#f8f9fa',
        borderRight: 'none',
      }}
    >
      {/* Brand area */}
      <Box
        component={RouterLink}
        to="/"
        onClick={handleClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2.5,
          py: 2,
          textDecoration: 'none',
          color: 'inherit',
        }}
      >
        <Box
          component="img"
          src={`${process.env.PUBLIC_URL}/favicon-192.png`}
          alt="Full Frame"
          sx={{
            width: 36,
            height: 36,
            flexShrink: 0,
            objectFit: 'contain',
          }}
        />
        <Box>
          <Typography sx={{ fontFamily: '"Manrope", system-ui, sans-serif', fontWeight: 800, fontSize: '1.25rem', letterSpacing: '-0.02em', color: '#2d3335', lineHeight: 1 }}>
            Full Frame
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
            <Typography sx={{ fontSize: '0.55rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.18em', color: '#adb3b5' }}>
              Intern Portaal
            </Typography>
            <Tooltip arrow placement="bottom-start" enterDelay={150}
              title={
                <Box sx={{ p: 0.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>Laatste wijzigingen</Typography>
                  <Typography variant="caption" sx={{ opacity: 0.7, display: 'block', mb: 0.5 }}>
                    v{packageJson.version} · {versionInfo.releaseDate}
                  </Typography>
                  {versionInfo.changes.map((c, i) => (
                    <Typography key={i} variant="caption" display="block">{c}</Typography>
                  ))}
                </Box>
              }>
              <Typography sx={{ fontSize: '0.55rem', fontFamily: 'monospace', color: '#94a3b8', fontWeight: 500, cursor: 'help' }}>
                v{packageJson.version}
              </Typography>
            </Tooltip>
          </Box>
        </Box>
      </Box>

      <Divider />

      {/* Zoekveld boven de navigatie — opent de globale zoeker (die focust zijn
          eigen invoerveld), dus klikken en meteen typen. ⌘K blijft ook werken. */}
      {onSearchClick && (
        <Box sx={{ px: 2, pt: 1.25, pb: 0.25 }}>
          <Box
            onClick={() => { onSearchClick(); if (isMobile && onClose) onClose(); }}
            sx={{
              display: 'flex', alignItems: 'center', gap: 1,
              px: 1.25, py: 0.75, borderRadius: '0.5rem',
              border: '1px solid #e5e7eb', bgcolor: '#fff', cursor: 'pointer',
              color: '#94a3b8',
              '&:hover': { borderColor: '#c9c3d8', bgcolor: '#fafafa' },
            }}
          >
            <SearchIcon sx={{ fontSize: 17 }} />
            <Typography sx={{ flex: 1, fontSize: '0.8rem', fontFamily: '"Inter", system-ui, sans-serif' }}>
              Zoeken…
            </Typography>
            <Typography sx={{ fontSize: '0.6rem', fontFamily: 'monospace', border: '1px solid #e5e7eb',
              borderRadius: 0.75, px: 0.5, py: 0.1, color: '#adb3b5' }}>
              ⌘K
            </Typography>
          </Box>
        </Box>
      )}

      {/* Primary nav */}
      <List sx={{ py: 1, flex: 0 }}>
        {items.primary.map(renderItem)}
      </List>

      {/* Tools section — only if items exist */}
      {items.tools.length > 0 && (
        <>
          <Divider sx={{ mx: 2 }} />
          <Box sx={{ overflow: 'auto', py: 1 }}>
            {items.toolsLabel && (
              <Typography
                variant="caption"
                fontWeight={700}
                color="text.disabled"
                sx={{ px: 2.5, py: 0.75, display: 'block', textTransform: 'uppercase', letterSpacing: '0.16em', fontSize: '0.58rem', fontFamily: '"Inter", system-ui, sans-serif' }}
              >
                {items.toolsLabel}
              </Typography>
            )}
            <List sx={{ py: 0 }}>
              {items.tools.map(renderItem)}
            </List>
          </Box>
        </>
      )}

      {/* Spacer */}
      <Box sx={{ flex: 1 }} />

      {/* Bottom area */}
      <Divider sx={{ mx: 2 }} />
      <List sx={{ py: 1 }}>
        {items.bottom.map(renderItem)}
      </List>

      {/* Visibility filters */}
      <Divider sx={{ mx: 2 }} />
      <VisibilityToggles />
    </Box>
  );
}

function VisibilityToggles() {
  const { showBeta, showPlanned, toggleBeta, togglePlanned } = useVisibilityFilters();
  const Pill = ({ active, onToggle, label, accent }) => (
    <Box
      onClick={onToggle}
      role="switch"
      aria-checked={active}
      sx={{
        flex: 1, cursor: 'pointer', userSelect: 'none',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        px: 1.25, py: 0.6, borderRadius: '999px',
        border: `1px solid ${active ? accent.color : 'rgba(45,51,53,0.1)'}`,
        bgcolor: active ? accent.bg : 'transparent',
        transition: 'all 0.12s',
        '&:hover': {
          borderColor: active ? accent.color : 'rgba(45,51,53,0.2)',
          bgcolor: active ? accent.bg : 'rgba(0,0,0,0.02)',
        },
      }}
    >
      <Typography sx={{
        fontFamily: '"Inter", system-ui, sans-serif',
        fontSize: '0.6rem', fontWeight: 800,
        textTransform: 'uppercase', letterSpacing: '0.08em',
        color: active ? accent.color : '#adb3b5',
      }}>
        {label}
      </Typography>
      <Box sx={{
        width: 22, height: 12, borderRadius: '999px',
        bgcolor: active ? accent.color : '#d4d6d7',
        position: 'relative', transition: 'background-color 0.12s',
        flexShrink: 0,
      }}>
        <Box sx={{
          position: 'absolute', top: 1, left: active ? 11 : 1,
          width: 10, height: 10, borderRadius: '999px', bgcolor: '#fff',
          transition: 'left 0.12s',
        }} />
      </Box>
    </Box>
  );

  return (
    <Box sx={{ px: 2, py: 1.25 }}>
      <Typography sx={{
        fontFamily: '"Inter", system-ui, sans-serif',
        fontSize: '0.55rem', fontWeight: 700,
        textTransform: 'uppercase', letterSpacing: '0.16em',
        color: '#adb3b5', mb: 1, px: 0.5,
      }}>
        Zichtbaarheid
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Pill
          active={showBeta} onToggle={toggleBeta} label="Beta"
          accent={{ color: '#7c3aed', bg: '#ede9fe' }}
        />
        <Pill
          active={showPlanned} onToggle={togglePlanned} label="Planned"
          accent={{ color: '#d97706', bg: '#fef3c7' }}
        />
      </Box>
    </Box>
  );
}

export default function SidebarNav({ mobileOpen, onMobileClose, hidePersistent, onSearchClick }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Mobile or full-width pages: only show drawer
  if (isMobile || hidePersistent) {
    return (
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          '& .MuiDrawer-paper': {
            width: SIDEBAR_WIDTH,
            boxSizing: 'border-box',
            border: 'none',
          },
        }}
      >
        <SidebarContent onClose={onMobileClose} onSearchClick={onSearchClick} />
      </Drawer>
    );
  }

  // Desktop: persistent sidebar
  return (
    <Box
      component="nav"
      sx={{
        width: SIDEBAR_WIDTH,
        flexShrink: 0,
        height: '100vh',
        position: 'sticky',
        top: 0,
      }}
    >
      <SidebarContent onSearchClick={onSearchClick} />
    </Box>
  );
}

export { SIDEBAR_WIDTH };
